import { useEffect, useCallback } from 'react';

interface UseMediaSessionParams {
  track: string | null;
  artist: string | null;
  isPlaying: boolean;
  onPlay: () => Promise<void>;
  onPause: () => Promise<void>;
}

/**
 * Хук для управления Media Session API (медиа-плеер устройства)
 */
export const useMediaSession = ({
  track,
  artist,
  isPlaying,
  onPlay,
  onPause,
}: UseMediaSessionParams) => {
  // Обновление Media Session метаданных
  const updateMediaSession = useCallback((track: string | null, artist: string | null, playing: boolean) => {
    if (!('mediaSession' in navigator)) {
      return;
    }

    const mediaSession = navigator.mediaSession;
    const baseUrl = window.location.origin;
    
    // Пути к обложкам (WebP для современных устройств, PNG для старых iOS)
    const coverWebP = `${baseUrl}/assets/images/afrodita_logo.webp`;
    const coverPNG = `${baseUrl}/assets/images/afrodita_logo.png`;
    
    // Формируем artwork с поддержкой WebP и PNG fallback для старых iOS
    // MediaSession API имеет ограничение на количество размеров (максимум ~7-8)
    // Оставляем только самые важные размеры для оптимальной производительности
    const artwork: MediaImage[] = [
      // WebP варианты (приоритет для современных устройств)
      { src: coverWebP, sizes: '1024x1024', type: 'image/webp' },
      { src: coverWebP, sizes: '512x512', type: 'image/webp' },
      { src: coverWebP, sizes: '256x256', type: 'image/webp' },
      { src: coverWebP, sizes: '128x128', type: 'image/webp' },
      // PNG fallback для старых iOS и устройств без поддержки WebP
      { src: coverPNG, sizes: '512x512', type: 'image/png' },
      { src: coverPNG, sizes: '256x256', type: 'image/png' },
      { src: coverPNG, sizes: '128x128', type: 'image/png' }
    ];

    const title = track || 'Радио Афродита';
    const album = 'Радио Афродита';

    try {
      // Упрощенная установка метаданных без предзагрузки
      mediaSession.metadata = new MediaMetadata({
        title: title,
        artist: artist || 'Радио Афродита',
        album: album,
        artwork: artwork
      });

      mediaSession.playbackState = playing ? 'playing' : 'paused';
    } catch (error) {
      console.warn('[MediaSession] Ошибка обновления метаданных:', error);
    }
  }, []);

  // Инициализация Media Session API
  useEffect(() => {
    if (!('mediaSession' in navigator)) {
      return;
    }

    const mediaSession = navigator.mediaSession;

    try {
      mediaSession.setActionHandler('play', async () => {
        await onPlay();
      });

      mediaSession.setActionHandler('pause', async () => {
        await onPause();
      });

      mediaSession.setActionHandler('previoustrack', null);
      mediaSession.setActionHandler('nexttrack', null);

      return () => {
        try {
          mediaSession.setActionHandler('play', null);
          mediaSession.setActionHandler('pause', null);
        } catch (error) {
          // Игнорируем ошибки при очистке
        }
      };
    } catch (error) {
      console.warn('[MediaSession] Ошибка инициализации:', error);
    }
  }, [onPlay, onPause]);

  // Обновление метаданных при изменении трека/артиста/состояния
  useEffect(() => {
    updateMediaSession(track, artist, isPlaying);
  }, [track, artist, isPlaying, updateMediaSession]);
};
